<template>
    <div class="foal-main-data-outer flex gap-5 mb-3 justify-content-between">
        <div class="foal-main-data flex flex-column w-5 gap-3 pb-3">
            <div class="flex gap-3 w-full">
                <CustomInputText name="name" :label="$t('message.horseCreate.name')"
                                 :required="computedRequired" class="w-full"
                                 v-model="horse.name" noPlaceholder :customErrorMessage="getRequiredMessage(horse.name)"
                                 :disable-brackets="true" force-uppercase/>
                <!-- <CustomInputText name="suffix" class="w-2 p-0" v-model="horse.suffix" noPlaceholder disabled/> -->
            </div>
            <div class="flex gap-3 w-full align-items-center">
                <CustomInputText name="arabian-name" class="w-full" v-model="horse.localName" noPlaceholder :customErrorMessage="getRequiredMessage(horse.localName)" dir="rtl"/>
                <div class="arabian-text">:{{ $t('message.horseCreate.arabianName') }}*</div>
            </div>
            <CustomSelectOneHorseSex name="sex" :label="$t('message.horseCreate.sex')"
                                     v-model="horse.sex" noPlaceholder :required="computedRequired" :customErrorMessage="getRequiredMessage(horse.sex)"/>
            <CustomSelectOneHorseColor name="colorId" :label="$t('message.horseCreate.coatColor')"
                                       v-model="horse.colorId" noPlaceholder :required="computedRequired"
                                       :customErrorMessage="getRequiredMessage(horse.colorId)"
                                       @validationResult="passColorValidationResult" :triggerValidation="triggerColorValidation"
                                       :damColorName="horse.damColorName" :sireColorName="horse.sireColorName"/>
            <CustomCalendar name="dateOfBirth" v-model="horse.dateOfBirth" :label="$t('message.horseCreate.dateOfBirth')"
                            :customErrorMessage="getRequiredMessage(horse.dateOfBirth)" required/>
            <CustomInputText name="sire" :label="$t('message.horseCreate.sire')"
                             :required="computedRequired"
                             v-model="horse.sireFullNameWithCountryCode" noPlaceholder disabled
                             :customErrorMessage="getRequiredMessage(horse.sireFullNameWithCountryCode)"/>
            <CustomInputText name="dam" :label="$t('message.horseCreate.dam')"
                             :required="computedRequired"
                             v-model="horse.damFullNameWithCountryCode" noPlaceholder disabled
                             :customErrorMessage="getRequiredMessage(horse.damFullNameWithCountryCode)"/>
            <CustomCalendar name="declarationDate" v-model="horse.dateOfDeclaration" :label="$t('message.horseCreate.declarationDate')"/>
            <div v-if="showLateAttention" class="flex align-items-center gap-2 late-attention-warning">
                <i style="color: red;" class="pi pi-exclamation-triangle attention-icon" />
                <span style="color: red;">{{ $t('message.horseCreate.attentionDeclaration') }}</span>
            </div>
            <CustomInputText name="strain" :label="$t('message.horseCreate.strain')" noPlaceholder
                             v-model="horse.strain" />
            <CustomSelectOneHorseClassification name="horseClassification" :label="$t('message.horseCreate.horseClassification')"
                                                v-model="horse.horseClassification" noPlaceholder />
        </div>
        <div class="horse-foal-owners flex flex-column w-5 pr-8">
            <HorseEditOwners v-model="horse" vertical :showErrorMessages="showErrorMessages"/>
        </div>
    </div>
</template>

<script>
import CustomButton from "@/components/form/CustomButton.vue";
import Toast from "primevue/toast";
import CustomInputTextArea from "@/components/form/CustomInputTextArea.vue";
import CustomInputText from "@/components/form/CustomInputText.vue";
import CustomSelectOne from "@/components/form/inner/CustomSelectOne.vue";
import CustomCheckbox from "@/components/form/CustomCheckbox.vue";
import CustomInputNumber from "@/components/form/CustomInputNumber.vue";
import CustomSelectOneHorseSex from "@/components/form/CustomSelectOneHorseSex.vue";
import CustomSelectOneHorseColor from "@/components/form/CustomSelectOneHorseColor.vue";
import CustomTextButton from "@/components/form/CustomTextButton.vue";
import CustomCalendar from "@/components/form/CustomCalendar.vue";
import HorseEditOwners from "@/views/horse/horseEdit/main/components/HorseEditOwners.vue";
import {SystemRole} from "@/utils/SystemRole";
import CustomSelectOneHorseClassification from "@/components/form/CustomSelectOneHorseClassification.vue";
import { DateUtils } from "@/utils/DateUtils";

export default {
    name: "FoalMainData",
    components: {
        HorseEditOwners,
        CustomCalendar,
        CustomTextButton,
        CustomSelectOneHorseColor,
        CustomSelectOneHorseSex,
        CustomSelectOneHorseClassification,
        CustomInputNumber,
        CustomCheckbox,
        CustomSelectOne,
        CustomInputText,
        CustomInputTextArea,
        CustomButton,
        Toast,
    },

    props: {
        modelValue: {
            type: Object,
            required: true,
        },
        showErrorMessages: {
            type: Boolean,
            default: false,
        },
        imported: {
            type: Boolean,
            default: false,
        },
        locallyBreed: {
            type: Boolean,
            default: false,
        },
        systemRole: {
            type: String,
            required: true,
        },
        triggerColorValidation: {
            type: Boolean,
            default: false,
        }
    },

    data() {
        return {
            showLateAttention: false,
        };
    },

    emits: ["update:modelValue", "color-validation-result", "gestation-period-validation-result"],

    mounted() {
        if (this.imported) {
            this.horse.imported = true;
            this.horse.locallyBreed = false;
        }
        if (this.locallyBreed) {
            this.horse.imported = false;
            this.horse.locallyBreed = true;
        }
    },

    methods: {
        getRequiredMessage(value) {
            if (this.showErrorMessages && (value === null || value === "")) {
                return this.$t("message.validations.required");
            }
            return "";
        },
        notifyDelete() {
            this.$toast.add({
                severity: "success",
                summary: this.$t('message.other.success'),
                detail: this.$t('message.other.elementDeleted'),
                life: 3000,
            });
        },
        notifyError(error) {
            const message = error.response?.data?.message || this.$t('unknownError');
            this.$toast.add({
                severity: "error",
                summary: this.$t('message.other.error'),
                detail: message,
                life: 5000,
            });
        },
        passColorValidationResult(isValid) {
            this.$emit('color-validation-result', isValid);
        },
        validateGestationPeriod() {
            this.$emit('gestation-period-validation-result');
        },
    },

    computed: {
        horse: {
            get() { return this.modelValue; },
            set(value) { this.$emit("update:modelValue", value); },
        },
        computedRequired() {
            const allowedRoles = [
                SystemRole.FIRST_LEVEL_EDITOR,
                SystemRole.SECOND_LEVEL_EDITOR,
                SystemRole.ADMIN,
            ];

            return allowedRoles.includes(this.systemRole)
        },
    },

    watch: {
        "horse.dateOfDeclaration"(value) {
            const dayDiff = (new Date(DateUtils.formatDateFromString(value, "DD-MM-YYYY", "YYYY-MM-DD")) - new Date(DateUtils.formatDateFromString(this.horse.dateOfBirth, "DD-MM-YYYY", "YYYY-MM-DD"))) / (1000 * 3600 * 24);
            if (dayDiff > 30) {
                this.showLateAttention = true;
            } else {
                this.showLateAttention = false;
            }
        },
        "horse.dateOfBirth": 'validateGestationPeriod',
        "horse.lastCoveringDate": 'validateGestationPeriod',
    },
};
</script>

<style lang="scss">
@import "../../../../assets/theme/mytheme/variables";
.foal-main-data-outer {
    @media screen and (max-width: 1300px) {
        justify-content: center !important;
        gap: 4rem !important;
    }
    @media screen and (max-width: 1000px) {
        flex-direction: column;
        align-items: center;
        gap: 1rem !important;
        .horse-foal-owners {
            width: 75% !important;
            align-self: start;
        }
    }
    @media screen and (max-width: 700px) {
        align-items: end;
    }
    .foal-main-data {
        .arabian-text {
            font-size: 20px;
        }

        .attention-icon {
            font-size: 1.5rem;
        }

        .custom-input-text, .custom-input-number, .custom-select-one, .custom-calendar, .late-attention-warning {
            padding-left: 250px;
            .custom-label {
                z-index: 999;
                position: absolute;
                transform: translateX(-100%);
                left: 240px;
                text-align: right;
            }
        }

        @media screen and (max-width: 1300px) {
            .custom-input-text, .custom-input-number, .custom-select-one, .custom-calendar, .late-attention-warning {
                padding-left: 170px;
                .custom-label {
                    left: 160px;
                }
            }
        }
        @media screen and (max-width: 1000px) {
            .custom-input-text, .custom-input-number, .custom-select-one, .custom-calendar, .late-attention-warning {
                padding-left: 0px;
                .custom-label {
                    left: -10px;
                    max-width: 200px;
                }
            }
        }
        @media screen and (max-width: 700px) {
            width: 60% !important;
        }

        .separator {
            width: 1.5px;
            background-color: var(--separator-color);
        }
    }
}
</style>
